import * as Yup from 'yup';

const schema = Yup.object().shape({
	id: Yup.number().defined(),
	name: Yup.string().default('').required('Required'),
	desc: Yup.string().default('').nullable(),
	price: Yup.number().positive().default(0).required('Required'),
	discountedPrice: Yup.number().positive().optional(),
	// for ordering purpose
	index: Yup.number().nullable().defined(),
	quantity: Yup.number().optional(),
	isBulk: Yup.boolean().optional(),
});

const isFlatTravelFee = (id: string | number) => {
	return Number(id) === Number(process.env.REACT_APP_PES_FLAT_TRAVEL_FEE_TEST_ID);
};

const isFollowUpQuestionTest = (id: string | number) => {
	return Number(id) === Number(process.env.REACT_APP_PES_FOLLOW_UP_QUESTION_TEST_ID);
};

const isAudiometry = (test: Pick<TestProps, 'name'>) => {
	return test.name.toLowerCase().includes('audiometry');
};

const isInstantDrugAlcoholScreen = (test: Pick<TestProps, 'name'>) => {
	return test.name.toLowerCase().includes('instant drug') && test.name.toLowerCase().includes('alcohol screen');
};

const isUnrineInstantDrugAlcoholTest = (test: Pick<TestProps, 'name'>) => {
	return test.name.toLowerCase().includes('urine') && test.name.toLowerCase().includes('instant drug') && test.name.toLowerCase().includes('alcohol test');
};

// test in payload now needs to have a quantity field as we introduce bulk request
// 1 indicates a single request
const normalise = (test: TestProps) => {
	return {
		...test,
		quantity: 1,
	};
};

const normaliseBulkTest = (tests: TestProps[], isOnsite: boolean) => {
	if (isOnsite) {
		return tests.map((test) => {
			if (isFlatTravelFee(test.id)) {
				return {
					...test,
					quantity: test.quantity ?? 1,
					min: 0,
				};
			}
			return test;
		});
	}

	return tests.map((test) => {
		if (isFlatTravelFee(test.id)) {
			return {
				...test,
				quantity: 0,
				min: 0,
			};
		}
		return test;
	});
};

const updateTestQty = (tests: TestProps[], id: TestProps['id'], quantity: number): TestProps[] => {
	return tests.map((t) => (Number(t.id) === Number(id) ? { ...t, quantity } : t));
};

const removeNilTest = (tests: TestProps[]): TestProps[] => {
	return tests.filter((t) => {
		return t.quantity || false;
	});
};

const applyDiscount = (tests: TestProps[], discountedTests: TestProps[]) => {
	const discountedPrices = new Map(discountedTests.map((test) => [Number(test.id), test.price]));

	return tests.map((test) => {
		const discountedPrice = discountedPrices.get(Number(test.id));

		if (discountedPrice === undefined) {
			return test;
		}

		return {
			...test,
			price: discountedPrice,
		};
	});
};

export const Test = {
	schema,
	isFlatTravelFee,
	isFollowUpQuestionTest,
	isAudiometry,
	isUnrineInstantDrugAlcoholTest,
	isInstantDrugAlcoholScreen,
	normaliseBulkTest,
	normalise,
	removeNilTest,
	updateTestQty,
	applyDiscount,
};

export type TestProps = Yup.InferType<typeof schema> & {
	min?: number;
};
